import { useEffect, useState } from "react";
import { theme, FONT_RUNE } from "../theme";
import { useAuth } from "../../auth/useAuth";
import { listGames, type GameSummary } from "../../games/api";
import * as S from "./sheet";

type Status =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ok"; games: GameSummary[]; nextCursor: string | null };

/**
 * Paginated list of the signed-in user's saved games. Each row opens the replay viewer.
 *
 * Pagination uses the server's cursor (the prior page's last started_at), so the "Load more"
 * button just appends the next page rather than re-querying the whole list.
 */
export function GameHistoryScreen({
  onClose,
  onOpenReplay,
}: {
  onClose: () => void;
  onOpenReplay: (gameId: string) => void;
}) {
  const { user } = useAuth();
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    void (async () => {
      const result = await listGames({ limit: 30 });
      if (!result) {
        setStatus({ kind: "error", message: "Couldn't load your games. Check your connection and try again." });
        return;
      }
      setStatus({ kind: "ok", games: result.games, nextCursor: result.nextCursor });
    })();
  }, []);

  const loadMore = async () => {
    if (status.kind !== "ok" || !status.nextCursor) return;
    setLoadingMore(true);
    const result = await listGames({ cursor: status.nextCursor, limit: 30 });
    setLoadingMore(false);
    if (!result) return;
    setStatus({
      kind: "ok",
      games: [...status.games, ...result.games],
      nextCursor: result.nextCursor,
    });
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.card, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <span style={S.title}>Your games</span>
          <button onClick={onClose} style={S.closeButton} aria-label="Close">×</button>
        </div>
        <div style={{ ...S.body, padding: "0 0 12px", gap: 0 }}>
          {status.kind === "loading" && <Empty>Loading…</Empty>}
          {status.kind === "error" && <Empty error>{status.message}</Empty>}
          {status.kind === "ok" && status.games.length === 0 && (
            <Empty>No saved games yet. Play one against the bot or online and it'll show up here.</Empty>
          )}
          {status.kind === "ok" &&
            status.games.map((game) => (
              <GameRow
                key={game.id}
                game={game}
                currentUserId={user?.id ?? null}
                onClick={() => onOpenReplay(game.id)}
              />
            ))}
          {status.kind === "ok" && status.nextCursor && (
            <button onClick={loadMore} disabled={loadingMore} style={loadMoreButton}>
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function GameRow({
  game,
  currentUserId,
  onClick,
}: {
  game: GameSummary;
  currentUserId: string | null;
  onClick: () => void;
}) {
  const date = formatDate(game.startedAt);
  const userSide: "white" | "black" | null =
    !currentUserId
      ? null
      : game.whiteUserId === currentUserId
        ? "white"
        : game.blackUserId === currentUserId
          ? "black"
          : null;

  const opponentLabel = game.mode === "bot" ? "Bot" : userSide ? `${oppositeSide(userSide)} player` : "Online";

  let outcome: string;
  let outcomeColor: string = theme.parchment;
  if (!game.endedAt) {
    outcome = "in progress";
    outcomeColor = theme.boardLabel;
  } else if (!game.winnerSide) {
    outcome = "ended";
    outcomeColor = theme.boardLabel;
  } else if (userSide === game.winnerSide) {
    outcome = "Won";
    outcomeColor = theme.legalMove;
  } else if (userSide && game.winnerSide && userSide !== game.winnerSide) {
    outcome = "Lost";
    outcomeColor = theme.captureFlash;
  } else {
    outcome = `${capitalize(game.winnerSide)} won`;
  }

  return (
    <button onClick={onClick} style={rowButton}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span style={{ fontFamily: FONT_RUNE, fontSize: 14, color: theme.parchment }}>
          {game.mode === "bot" ? "vs Bot" : "Online"}
          {userSide && <span style={{ color: theme.boardLabel, fontSize: 11, marginLeft: 6 }}>· you played {userSide}</span>}
          {!userSide && game.mode === "online" && <span style={{ color: theme.boardLabel, fontSize: 11, marginLeft: 6 }}>· {opponentLabel}</span>}
        </span>
        <span style={{ fontSize: 12, color: theme.boardLabel }}>{date}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
        <span style={{ fontFamily: FONT_RUNE, fontSize: 12, color: theme.accentSoft, letterSpacing: 1 }}>
          {game.victoryLevel.toUpperCase()}
          {game.threshold ? ` · ${game.threshold}` : ""}
        </span>
        <span style={{ fontSize: 13, color: outcomeColor }}>{outcome}</span>
      </div>
      {game.endedAt && (game.whiteFinalScore != null || game.blackFinalScore != null) && (
        <div style={{ marginTop: 4, fontSize: 11, color: theme.parchment, opacity: 0.6, fontFamily: FONT_RUNE }}>
          W {game.whiteFinalScore ?? "—"} · B {game.blackFinalScore ?? "—"}
        </div>
      )}
    </button>
  );
}

function Empty({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div
      style={{
        padding: "20px 18px",
        color: error ? theme.captureFlash : theme.parchment,
        opacity: error ? 1 : 0.8,
        fontSize: 14,
      }}
    >
      {children}
    </div>
  );
}

function oppositeSide(side: "white" | "black"): "white" | "black" {
  return side === "white" ? "black" : "white";
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) return "just now";
  if (diff < hour) return `${Math.floor(diff / minute)}m ago`;
  if (diff < day) return `${Math.floor(diff / hour)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return date.toLocaleDateString();
}

const rowButton: React.CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "12px 18px",
  background: "transparent",
  border: "none",
  borderTop: `1px solid ${theme.boardEdge}`,
  cursor: "pointer",
  color: theme.parchment,
};

const loadMoreButton: React.CSSProperties = {
  margin: "12px 18px 0",
  padding: "10px 14px",
  background: theme.boardEdge,
  color: theme.parchment,
  border: `1px solid ${theme.inkSoft}`,
  borderRadius: 6,
  fontFamily: FONT_RUNE,
  fontSize: 12,
  letterSpacing: 1,
  cursor: "pointer",
};
